import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/shared/utils/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const base =
  "inline-flex items-center justify-center gap-2 rounded-md border font-medium select-none whitespace-nowrap transition-colors duration-micro ease-brand disabled:opacity-50 disabled:cursor-not-allowed";

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-[var(--accent-600)] text-white border-[var(--accent-600)] hover:bg-[var(--accent-700)] hover:border-[var(--accent-700)] active:bg-[var(--accent-800)]",
  secondary:
    "bg-white text-gray-800 border-[var(--border)] hover:bg-[var(--gray-50)] hover:border-[var(--border-strong)]",
  ghost:
    "bg-transparent text-gray-700 border-transparent hover:bg-[var(--gray-100)]",
  danger:
    "bg-white text-[var(--danger-700)] border-[var(--danger-100)] hover:bg-[var(--danger-50)] hover:border-[var(--danger-600)]",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-7 px-2.5 text-[12px]",
  md: "h-8 px-3 text-[13px]",
  lg: "h-10 px-4 text-[14px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(base, variantClasses[variant], sizeClasses[size], className)}
      {...rest}
    >
      {loading ? <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden /> : null}
      {children}
    </button>
  );
});
